import React from 'react';
import {
  Box, Card, CardContent, Chip, Tooltip, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SEARCE_COLORS } from '../../theme';
import type { AssetResponse } from '../../types/assets';
import { trimText } from '../../utils/assetHelpers';

interface AssetCardProps {
  asset: AssetResponse;
  onClick: (asset: AssetResponse) => void;
}

const AssetCard: React.FC<AssetCardProps> = ({ asset, onClick }) => {
  const cloudServices = asset.key_cloud_services ?? [];
  const marketplaceListed = Boolean(asset.marketplace_listing_status);

  return (
    <Card
      className="asset-card"
      onClick={() => onClick(asset)}
      sx={{
        height: '100%',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '15px',
        border: `1px solid ${alpha(SEARCE_COLORS.primary, 0.18)}`,
        borderLeft: `4px solid ${SEARCE_COLORS.primary}`,
        background: `linear-gradient(145deg, ${SEARCE_COLORS.white} 0%, ${alpha(SEARCE_COLORS.primary, 0.06)} 55%, ${alpha(SEARCE_COLORS.primary_light, 0.12)} 100%)`,
        boxShadow: `0 2px 8px ${alpha(SEARCE_COLORS.primary, 0.08)}`,
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease, opacity 0.2s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          background: `linear-gradient(145deg, ${alpha(SEARCE_COLORS.primary, 0.08)} 0%, ${alpha(SEARCE_COLORS.primary_light, 0.18)} 50%, ${alpha(SEARCE_COLORS.info, 0.1)} 100%)`,
          boxShadow: `0 8px 24px ${alpha(SEARCE_COLORS.primary, 0.28)}`,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 0.5, mb: 0.75 }}>
          <Tooltip title={asset.asset_name} arrow>
            <Typography
              variant="body2"
              component="h3"
              fontWeight={600}
              sx={{
                fontSize: '0.875rem',
                color: SEARCE_COLORS.primary_dark,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minHeight: '2.4em',
                flex: 1,
                minWidth: 0,
              }}
            >
              {trimText(asset.asset_name, 60)}
            </Typography>
          </Tooltip>
          {asset.asset_type && (
            <Chip
              label={asset.asset_type}
              size="small"
              sx={{
                fontSize: '0.55rem',
                height: 20,
                flexShrink: 0,
                fontWeight: 600,
                backgroundColor: SEARCE_COLORS.primary,
                color: SEARCE_COLORS.on_primary,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )}
        </Box>

        <Tooltip title={asset.short_description?.trim() || 'No short description'} arrow>
          <Typography
            variant="caption"
            sx={{
              mb: 1,
              color: SEARCE_COLORS.primary_700,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minHeight: '2.2em',
              lineHeight: 1.35,
            }}
          >
            {asset.short_description?.trim() || '—'}
          </Typography>
        </Tooltip>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.35,
            pt: 0.75,
            borderTop: `1px solid ${alpha(SEARCE_COLORS.primary, 0.12)}`,
          }}
        >
          <Typography variant="caption" sx={{ color: SEARCE_COLORS.primary_700 }} noWrap>
            <Box component="span" fontWeight={700} sx={{ color: SEARCE_COLORS.primary }}>BU:</Box>{' '}
            {asset.submitter_business_unit?.trim() || '—'}
          </Typography>
          <Tooltip title={asset.submitted_by || ''} arrow>
            <Typography variant="caption" sx={{ color: SEARCE_COLORS.primary_700 }} noWrap>
              <Box component="span" fontWeight={700} sx={{ color: SEARCE_COLORS.primary }}>Owner:</Box>{' '}
              {trimText(asset.submitted_by, 28) || '—'}
            </Typography>
          </Tooltip>
          <Tooltip title={cloudServices.join(', ') || '—'} arrow>
            <Typography variant="caption" sx={{ color: SEARCE_COLORS.primary_700 }} noWrap>
              <Box component="span" fontWeight={700} sx={{ color: SEARCE_COLORS.primary }}>Key cloud services:</Box>{' '}
              {cloudServices.length > 0 ? trimText(cloudServices.join(', '), 36) : '—'}
            </Typography>
          </Tooltip>
          <Typography variant="caption" sx={{ color: SEARCE_COLORS.primary_700 }} noWrap>
            <Box component="span" fontWeight={700} sx={{ color: SEARCE_COLORS.primary }}>Marketplace:</Box>{' '}
            {marketplaceListed ? 'Listed' : 'Not listed'}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AssetCard;
